import { useState, useEffect } from 'react';
import { Newspaper, User, TrendingUp } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import toast from 'react-hot-toast';

interface DigestPreferences {
  enabled: boolean;
  digestType: 'personal' | 'instance';
  emailDigests: boolean;
}

export default function DigestPreferences() {
  const token = useAuthStore((state) => state.token)!;
  const [preferences, setPreferences] = useState<DigestPreferences>({
    enabled: true,
    digestType: 'personal',
    emailDigests: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  async function fetchPreferences() {
    try {
      const response = await fetch('/api/digests/preferences', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch preferences');

      const data = await response.json();
      setPreferences({
        enabled: data.enabled,
        digestType: data.digestType,
        emailDigests: data.emailDigests ?? true,
      });
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }

  async function savePreferences(newPreferences: Partial<DigestPreferences>) {
    setSaving(true);
    try {
      const response = await fetch('/api/digests/preferences', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPreferences),
      });

      if (!response.ok) throw new Error('Failed to save preferences');

      const data = await response.json();
      setPreferences({
        enabled: data.enabled,
        digestType: data.digestType,
        emailDigests: data.emailDigests ?? true,
      });
      toast.success('Preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  function handleEnabledChange(enabled: boolean) {
    savePreferences({ enabled });
  }

  function handleTypeChange(digestType: 'personal' | 'instance') {
    savePreferences({ digestType });
  }

  function handleEmailDigestsChange(emailDigests: boolean) {
    savePreferences({ emailDigests });
  }

  if (loading) {
    return (
      <div className="bg-canvas dark:bg-gray-800 rounded-lg border border-edge dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-canvas dark:bg-gray-800 rounded-lg border border-edge dark:border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Newspaper className="w-6 h-6 text-accent-500" />
        <div>
          <h2 className="text-xl font-semibold text-ink dark:text-white">
            Digest Preferences
          </h2>
          <p className="text-sm text-ink-muted">
            Configure your weekly AI digest settings
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between py-4 border-b border-edge dark:border-gray-700">
          <div>
            <h3 className="font-medium text-ink dark:text-white">
              Automatic Weekly Digests
            </h3>
            <p className="text-sm text-ink-muted mt-1">
              Receive a weekly digest every Sunday morning
            </p>
          </div>
          <button
            onClick={() => handleEnabledChange(!preferences.enabled)}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences.enabled
                ? 'bg-primary-600'
                : 'bg-gray-200 dark:bg-gray-700'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-canvas transition-transform ${
                preferences.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Digest Type Selection */}
        <div>
          <h3 className="font-medium text-ink dark:text-white mb-3">
            Digest Type
          </h3>
          <p className="text-sm text-ink-muted mb-4">
            Choose what you'd like to see in your weekly digest
          </p>

          <div className="grid gap-3">
            {/* Personal Digest Option */}
            <button
              onClick={() => handleTypeChange('personal')}
              disabled={saving || !preferences.enabled}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                preferences.digestType === 'personal'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${
                !preferences.enabled || saving
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${
                  preferences.digestType === 'personal'
                    ? 'text-accent-600 dark:text-accent-400'
                    : 'text-gray-400'
                }`}>
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-ink dark:text-white mb-1">
                    Personal Digest
                  </div>
                  <p className="text-sm text-ink-muted">
                    See a summary of your own reading activity, likes, shares, and bookmarks from the past week
                  </p>
                </div>
                <div className="ml-auto">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    preferences.digestType === 'personal'
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {preferences.digestType === 'personal' && (
                      <div className="w-2 h-2 bg-canvas rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            </button>

            {/* Instance Digest Option */}
            <button
              onClick={() => handleTypeChange('instance')}
              disabled={saving || !preferences.enabled}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                preferences.digestType === 'instance'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${
                !preferences.enabled || saving
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${
                  preferences.digestType === 'instance'
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-400'
                }`}>
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-ink dark:text-white mb-1">
                    Instance Digest
                  </div>
                  <p className="text-sm text-ink-muted">
                    See what's trending across your entire instance - the most shared articles and community activity
                  </p>
                </div>
                <div className="ml-auto">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    preferences.digestType === 'instance'
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {preferences.digestType === 'instance' && (
                      <div className="w-2 h-2 bg-canvas rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Email Digest Toggle */}
        <div className="flex items-center justify-between py-4 border-t border-edge dark:border-gray-700">
          <div>
            <h3 className="font-medium text-ink dark:text-white">
              Receive Digest via Email
            </h3>
            <p className="text-sm text-ink-muted mt-1">
              Get your weekly digest delivered to your inbox
            </p>
          </div>
          <button
            onClick={() => handleEmailDigestsChange(!preferences.emailDigests)}
            disabled={saving || !preferences.enabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences.emailDigests
                ? 'bg-primary-600'
                : 'bg-gray-200 dark:bg-gray-700'
            } ${
              saving || !preferences.enabled
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-canvas transition-transform ${
                preferences.emailDigests ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
